import React from "react";
import PropTypes from "prop-types";
import PriceCheckSummary from "./PriceCheckSummary";
import PriceCheckFilters from "./PriceCheckFilters";
import PriceCheckTabs from "./PriceCheckTabs";
import PriceCheckTable from "./PriceCheckTable";
import PriceCheckEmptyState from "./PriceCheckEmptyState";
import { DEFAULT_COLUMNS } from "../../pages/price-check/utils";

const PriceCheckMainContent = ({
  formValues,
  searchFormValues,
  properties,
  setIsDrawerOpen,
  setIsShowHideColumn,
  isPilotHotel = false,
  filtersData,
  onFiltersChange,
  sortState,
  currentFilters,
  currentFilterSelectionOrder,
  searchCounter,
  isLoading,
  priceCheckData,
  activeTab,
  setActiveTab,
  columns = DEFAULT_COLUMNS,
  priceCheckPage,
  onPageChange,
  expandedRowKeys,
  handleToggleExpand,
  handleSort,
  setHasSearchResults,
  onTabChange,
  setPriceCheckPage,
  searchBasedPropertyObjects = [],
  tabHandlers = {},
}) => {
  // Filter data for the selected tab
  // activeTab is now a propertyId (or "all")
  const filteredData =
    activeTab === "all"
      ? priceCheckData
      : priceCheckData.filter((row) => {
          // Find the property object to get the property name
          const propertyObj = searchBasedPropertyObjects.find(
            (p) => p.propertyId === activeTab
          );
          if (propertyObj) {
            // Match by propertyId if row has it, otherwise match by propertyName
            return (
              row.propertyId === activeTab ||
              row.propertyName === propertyObj.propertyName
            );
          }
          // Fallback: try to match by propertyName (for backward compatibility)
          return row.propertyName === activeTab;
        });

  return (
    <>
      <p className="page-header">Price Check</p>
      <PriceCheckSummary
        formValues={formValues}
        properties={properties}
        setIsDrawerOpen={setIsDrawerOpen}
      />
      <div className="price-check-data">
        <div className="price-check-data-body">
          <PriceCheckFilters
            key={`filters-${searchCounter}`} // Force re-render when search counter changes
            setIsShowHideColumn={setIsShowHideColumn}
            isPilotHotel={isPilotHotel}
            filtersData={filtersData}
            onFiltersChange={onFiltersChange}
            formValues={formValues}
            sortState={sortState}
            currentFilters={currentFilters}
            currentFilterSelectionOrder={currentFilterSelectionOrder}
            properties={properties}
            columns={columns}
            setPriceCheckPage={setPriceCheckPage}
            searchBasedPropertyNames={searchBasedPropertyObjects}
            tabHandlers={tabHandlers}
            activeTab={activeTab}
          />
          {/* Check if we should show complete empty state or tabs */}
          {!isLoading && priceCheckData.length === 0 && activeTab === "all" ? (
            // Show complete empty state when:
            // 1. No properties/cities are selected at all, OR
            // 2. No data available for ALL selected properties (when on "all" tab)
            <PriceCheckEmptyState
              onSearchAgain={() => {
                setIsDrawerOpen(true);
                setHasSearchResults(false); // Reset search results flag when starting new search
              }}
            />
          ) : (
            <>
              <PriceCheckTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                properties={properties}
                selectedCity={formValues?.selectedCity}
                selectedProperties={formValues?.selectedProperties}
                onTabChange={onTabChange}
                searchBasedPropertyObjects={searchBasedPropertyObjects}
              />
              {!isLoading && filteredData.length === 0 ? (
                // Show tab-specific empty state when filtered data is empty for this tab but overall data exists
                <PriceCheckEmptyState
                  onSearchAgain={() => {
                    setIsDrawerOpen(true);
                    setHasSearchResults(false); // Reset search results flag when starting new search
                  }}
                />
              ) : (
                <PriceCheckTable
                  priceCheckData={filteredData}
                  columns={columns}
                  isLoading={isLoading}
                  priceCheckPage={priceCheckPage}
                  onPageChange={onPageChange}
                  expandedRowKeys={expandedRowKeys}
                  handleToggleExpand={handleToggleExpand}
                  handleSort={handleSort}
                  sortState={sortState}
                  formValues={searchFormValues || { adults: 2, children: 0 }}
                  key={`table-${searchCounter}`}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

PriceCheckMainContent.propTypes = {
  formValues: PropTypes.object.isRequired,
  searchFormValues: PropTypes.object,
  properties: PropTypes.array.isRequired,
  setIsDrawerOpen: PropTypes.func.isRequired,
  setIsShowHideColumn: PropTypes.func.isRequired,
  isPilotHotel: PropTypes.bool,
  filtersData: PropTypes.object.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  sortState: PropTypes.object.isRequired,
  currentFilters: PropTypes.object.isRequired,
  currentFilterSelectionOrder: PropTypes.array.isRequired,
  searchCounter: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  priceCheckData: PropTypes.array.isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  columns: PropTypes.array,
  priceCheckPage: PropTypes.object.isRequired,
  onPageChange: PropTypes.func.isRequired,
  expandedRowKeys: PropTypes.array.isRequired,
  handleToggleExpand: PropTypes.func.isRequired,
  handleSort: PropTypes.func.isRequired,
  setHasSearchResults: PropTypes.func.isRequired,
  onTabChange: PropTypes.func,
  setPriceCheckPage: PropTypes.func.isRequired,
  searchBasedPropertyObjects: PropTypes.arrayOf(
    PropTypes.shape({
      propertyId: PropTypes.string.isRequired,
      propertyName: PropTypes.string.isRequired,
    })
  ),
  tabHandlers: PropTypes.shape({
    getFilterStateForTab: PropTypes.func,
  }),
};

export default PriceCheckMainContent;
